import { cn } from "@/lib/utils";
import loadingGif from "@/assets/loading_animation.gif";

interface AnimatedLogoProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  animation?: "spin" | "pulse" | "bounce" | "float";
  className?: string;
  /**
   * "svg" (default) — lightweight inline SVG + CSS animation. Use on
   * critical render paths (splash, route loaders) so we don't ship a
   * 3MB GIF before the app is interactive.
   * "gif" — branded loading_animation.gif. Use only after first paint
   * (modals, in-app loaders).
   */
  variant?: "svg" | "gif";
  /** Overrides the svg variant's default text-primary-light color. */
  colorClassName?: string;
}

const sizeClasses = {
  xs: "h-8 w-8",
  sm: "h-12 w-12",
  md: "h-16 w-16",
  lg: "h-24 w-24",
  xl: "h-32 w-32",
  "2xl": "h-40 w-40",
};

const animationClasses: Record<
  NonNullable<AnimatedLogoProps["animation"]>,
  string
> = {
  spin: "animate-spin",
  pulse: "animate-pulse",
  bounce: "animate-bounce",
  float: "animate-pulse",
};

export function AnimatedLogo({
  size = "md",
  animation = "bounce",
  className,
  variant = "svg",
  colorClassName,
}: AnimatedLogoProps) {
  if (variant === "svg") {
    return (
      <div
        className={cn(
          "flex items-center justify-center",
          sizeClasses[size],
          className,
        )}
        role="img"
        aria-label="PayStreem Loading"
      >
        <svg
          viewBox="0 0 64 64"
          className={cn(
            "h-full w-full text-primary-light",
            animationClasses[animation],
            colorClassName,
          )}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="32"
            cy="32"
            r="26"
            stroke="currentColor"
            strokeOpacity="0.18"
            strokeWidth="6"
          />
          <path
            d="M32 6a26 26 0 0 1 26 26"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <path
            d="M26 22h10a8 8 0 0 1 0 16h-6v8h-4V22Zm4 4v8h6a4 4 0 0 0 0-8h-6Z"
            fill="currentColor"
          />
        </svg>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center",
        sizeClasses[size],
        className,
      )}
      role="img"
      aria-label="PayStreem Loading"
    >
      <img
        src={loadingGif}
        alt="PayStreem Loading"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        className="h-full w-full object-contain"
        onError={(e) => {
          const target = e.currentTarget;
          target.style.display = "none";
          const fallback = document.createElement("div");
          fallback.innerHTML = `<svg class="h-full w-full text-primary" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>`;
          target.parentNode?.appendChild(fallback);
        }}
      />
    </div>
  );
}
